import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Settings, CreditCard, MessageSquare, LogOut, ChevronRight, Crown, Heart, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { API } from "../App";
import { logOut } from "../lib/firebase";

const Profile = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchSubscriptionStatus();
  }, [user, navigate]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/status`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      setUser(null);
      navigate("/");
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout");
    }
  };

  const getMessagesUsed = () => {
    return subscription?.messages_used || user?.messages_used || 0;
  };

  const getMessagesLimit = () => {
    const plan = subscription?.plan || user?.subscription_plan || "free";
    const limits = { free: 10, starter: 100, pro: 1000 };
    return limits[plan] || 10;
  };

  const getProgressPercent = () => {
    return (getMessagesUsed() / getMessagesLimit()) * 100;
  };

  const getPlanName = () => {
    const plan = subscription?.plan || user?.subscription_plan || "free";
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const menuItems = [
    {
      icon: MessageSquare,
      label: "Chat History",
      desc: "View past conversations",
      action: () => navigate("/chat"),
    },
    {
      icon: CreditCard,
      label: "Subscription",
      desc: `${getPlanName()} Plan`,
      action: () => navigate("/subscriptions"),
      badge: subscription?.plan !== "free" ? "Active" : null,
    },
    {
      icon: Heart,
      label: "Health Tips",
      desc: "Preventive care advice",
      action: () => navigate("/health-tips"),
    },
    {
      icon: Shield,
      label: "Privacy & Security",
      desc: "Manage your data",
      action: () => navigate("/privacy"),
    },
    {
      icon: Settings,
      label: "Settings",
      desc: "App preferences",
      action: () => navigate("/settings"),
    },
  ];

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-slate-800">Profile</h1>
              <p className="text-xs text-slate-500">Manage your account</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* User Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-3xl p-6 mb-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="w-16 h-16 border-2 border-white shadow-lg">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-primary-100 text-primary-600 font-semibold text-lg">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-800">{user?.name}</h2>
              <p className="text-sm text-slate-500">{user?.email}</p>
              {subscription?.plan !== "free" && (
                <div className="flex items-center gap-1 mt-1">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-600">{getPlanName()} Member</span>
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          <div className="bg-white/50 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">Messages Used</span>
              <span className="text-sm font-semibold text-primary-600">
                {getMessagesUsed()} / {getMessagesLimit()}
              </span>
            </div>
            <Progress value={getProgressPercent()} className="h-2" />
            {getProgressPercent() >= 80 && (
              <p className="text-xs text-amber-600 mt-2">
                Running low on messages. 
                <button 
                  onClick={() => navigate("/subscriptions")} 
                  className="text-primary-600 font-medium ml-1 hover:underline"
                >
                  Upgrade now
                </button>
              </p>
            )}
          </div>
        </motion.div>

        {/* Menu Items */}
        <div className="space-y-3">
          {menuItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-card rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-white/80 transition-all"
              onClick={item.action}
              data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
                <item.icon className="w-5 h-5 text-primary-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800">{item.label}</h3>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
              {item.badge && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                  {item.badge}
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </motion.div>
          ))}
        </div>

        {/* Logout Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <Button
            variant="ghost"
            className="w-full rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </motion.div>

        {/* App Info */}
        <div className="text-center mt-8">
          <p className="text-xs text-slate-400">SupaDoc AI v1.0.0</p>
          <p className="text-xs text-slate-400 mt-1">© 2025 SupaDoc. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Profile;
